import { PaymentOption } from '@navio/nsdk';
import type { BookingUserNotificationDto } from 'noddi-types';
import { trackBookingCreated, useAuthContext } from 'noddi-web';
import {
  ConfirmStationaryBookingButton,
  type InvoiceReferenceInput
} from '../../../../components/BookingFlow/Stationary/ConfirmStationaryBookingButton';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import {
  useStationaryAddonServices,
  useStationaryBookingCar,
  useStationaryIncomingTireSeason,
  useStationaryPrimaryService,
  useStationaryReservation
} from '../../../../stores/StationaryBookingStore';
import { buildStationaryCreateBody } from '../../../../utils/stationaryConfirm';

interface ConfirmStationaryBookingProps {
  paymentOption: PaymentOption;
  invoiceReference?: InvoiceReferenceInput;
  userGroupPayerId?: number | null;
  /** Full checkout total (services + open order) — same source as the summary card. */
  totalAmount: number;
  hasExistingBooking: boolean;
  /** Block confirm when account phone is invalid (core booking uses profile phone). */
  hasValidPhone: boolean;
  hasOrganizationNumber: boolean;
  /** Single owner from summary hook — do not re-resolve org UG here. */
  bookingUserGroupId: number | null;
  /** Person the organization booking is assigned to. Omitted, the signed-in user. */
  bookingUserId?: number | null;
  /** Per-booking SMS opt-ins (organization booker, silenced drivers); empty keeps the backend default. */
  notificationsToOverride: BookingUserNotificationDto[];
}

export const ConfirmStationaryBooking = ({
  paymentOption,
  invoiceReference,
  userGroupPayerId,
  totalAmount,
  hasExistingBooking,
  hasValidPhone,
  hasOrganizationNumber,
  bookingUserGroupId,
  bookingUserId,
  notificationsToOverride
}: ConfirmStationaryBookingProps) => {
  const selectedCar = useStationaryBookingCar();
  const reservation = useStationaryReservation();
  const primaryService = useStationaryPrimaryService();
  const addonServices = useStationaryAddonServices();
  const incomingTireSeason = useStationaryIncomingTireSeason();
  const { currentBrand } = useCurrentBrand();
  const { userData } = useAuthContext();

  return (
    <ConfirmStationaryBookingButton
      paymentOption={paymentOption}
      invoiceReference={invoiceReference}
      totalAmount={totalAmount}
      hasValidPhone={hasValidPhone}
      hasOrganizationNumber={hasOrganizationNumber}
      // Summary owns useCancelHoldOnExpiry and unmounts this when expired.
      disabled={hasExistingBooking}
      errorMode='toast'
      onBookingCreated={({ paymentMethod, bookingId, order }) => {
        trackBookingCreated({
          brand: currentBrand,
          paymentOption: paymentMethod,
          bookingId,
          locationType: 'stationary',
          order
        });
      }}
      buildCreateBody={() =>
        buildStationaryCreateBody({
          licensePlate: selectedCar?.licensePlate,
          hold: reservation,
          salesItemIds: [
            ...(primaryService ? [primaryService.salesItemId] : []),
            ...addonServices.map((s) => s.salesItemId)
          ],
          userId: bookingUserId ?? userData?.user.id,
          userGroupId: bookingUserGroupId,
          brandDomain: currentBrand,
          userGroupPayerId,
          notificationsToOverride,
          incomingTireSeason
        })
      }
    />
  );
};
